import { Component } from "react";

/**
 * Menangkap error render supaya satu halaman yang rusak tidak membuat seluruh
 * aplikasi jadi layar putih.
 *
 * Tanpa ini, satu exception di halaman mana pun membuat React melepas seluruh
 * pohon komponen: navigasi, header, semuanya ikut hilang, dan yang tersisa
 * hanya layar kosong tanpa pesan. Akibatnya kerusakan yang cuma di satu halaman
 * terlihat seperti aplikasi mati total, dan penyebabnya tidak terlihat sama
 * sekali -- harus dibuka lewat developer console yang tidak semua orang tahu
 * ke mana.
 *
 * Yang ditampilkan di sini sengaja dibuat terbaca orang, bukan developer:
 * pesan error aslinya, nama halaman, dan tombol untuk memuat ulang. Untuk
 * debugging, pesan aslinya ikut ditampilkan -- itu satu-satunya cara_user
 * bisa menggradienti masalahnya tanpa console.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    if (this.props.onError) {
      try {
        this.props.onError(error, info);
      } catch {
        // Pelapor error tidak boleh ikut menjatuhkan tampilan error.
      }
    }
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const where = this.props.label || "halaman ini";

    return (
      <div className="min-h-screen bg-[#FBFBF9] text-zinc-950 flex items-start justify-center px-6 py-16">
        <div className="max-w-2xl w-full">
          <h1 className="font-serif text-2xl mb-2">Halaman ini gagal dibuka</h1>
          <p className="text-sm text-zinc-600 mb-6">
            Jadi masalahnya ada di {where}, bukan di aplikasi seluruhnya. Menu di
            atas masih bisa dipakai untuk pindah halaman lain.
          </p>

          <div className="border border-zinc-300 rounded-lg p-4 mb-6">
            <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">
              Pesan error
            </p>
            <p className="text-sm font-mono break-words">
              {error.message || String(error)}
            </p>
            {info?.componentStack ? (
              <details className="mt-4">
                <summary className="text-xs text-zinc-500 cursor-pointer">
                  Lokasi komponen
                </summary>
                <pre className="text-[11px] text-zinc-600 mt-2 whitespace-pre-wrap break-words">
                  {info.componentStack}
                </pre>
              </details>
            ) : null}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-zinc-950 text-white text-sm rounded hover:bg-zinc-800"
            >
              Muat ulang halaman
            </button>
            <a
              href="/dashboard"
              className="px-4 py-2 border border-zinc-300 text-sm rounded hover:border-zinc-950"
            >
              Ke Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}